import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { SiteLayout } from "@/components/site/SiteLayout";
import { Clock, User, ChevronRight, Loader2 } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null); setNotFound(false);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <SiteLayout>
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <h1 className="text-3xl font-bold text-white">Yazı Bulunamadı</h1>
        <Link to="/blog" className="btn-gradient inline-block mt-6 px-6 py-3 rounded-xl font-bold">Blog'a Dön</Link>
      </div>
    </SiteLayout>
  );

  if (!post) return <SiteLayout><div className="py-32 flex justify-center"><Loader2 className="animate-spin text-white" /></div></SiteLayout>;

  const url = `https://takipcisatis.com/blog/${post.slug}`;
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "Article", headline: post.title, description: post.excerpt,
      image: post.cover_image || post.og_image, datePublished: post.published_at, dateModified: post.updated_at,
      author: { "@type": "Organization", name: post.author || "TakipciSatis" },
      publisher: { "@type": "Organization", name: "TakipciSatis.com", logo: { "@type": "ImageObject", url: "https://takipcisatis.com/takipcisatis-logo.png" } },
      mainEntityOfPage: url },
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://takipcisatis.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://takipcisatis.com/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: url },
    ] },
  ];

  return (
    <SiteLayout>
      <Seo title={post.seo_title || post.title} description={post.seo_description || post.excerpt}
        image={post.og_image || post.cover_image} canonical={post.canonical || url}
        robots={post.robots} type="article" jsonLd={jsonLd} />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <nav className="flex items-center gap-1 text-xs text-gray-500 mb-6" data-testid="breadcrumb">
          <Link to="/" className="hover:text-white">Ana Sayfa</Link><ChevronRight size={12} />
          <Link to="/blog" className="hover:text-white">Blog</Link><ChevronRight size={12} />
          <span className="text-gray-300 truncate">{post.title}</span>
        </nav>
        {post.category && <span className="text-xs text-[#d8b4fe] font-semibold uppercase tracking-wider">{post.category}</span>}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white mt-2 leading-tight">{post.title}</h1>
        <div className="flex items-center gap-4 text-sm text-gray-400 mt-4">
          <span className="flex items-center gap-1"><User size={14} />{post.author || "TakipciSatis"}</span>
          <span className="flex items-center gap-1"><Clock size={14} />{(post.published_at || "").slice(0, 10)}</span>
        </div>
        {post.cover_image && <img src={post.cover_image} alt={post.title} className="w-full rounded-2xl mt-6 object-cover max-h-96" />}
        <div className="blog-content mt-8" dangerouslySetInnerHTML={{ __html: post.content }} data-testid="blog-body" />
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8">
            {post.tags.map((t) => (
              <Link key={t} to={`/blog?tag=${t}`} className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-gray-300 hover:bg-white/10">#{t}</Link>
            ))}
          </div>
        )}
      </article>
    </SiteLayout>
  );
}
